/**
 * AI 검토안 본문에서 목차(TOC) 항목을 추출한다.
 *
 * 보고서 위계(3계층):
 *  - 대분류: 섹션 마커 `[약사용 보고서]`/`[환자용 복약지도문]`/`[종사자용 안내문]`
 *            → `div.section-title`. 추가로 `분석결과 요약` badge도 의미상 약사용 보고서와
 *            동급(7단계 요약이 맨 앞으로 이동된 것)이라 대분류로 승격한다.
 *  - 중분류: badge `💊 약 복용 시 주의할 점`(환자 안내) / `🏥 ○○의원 선생님께`(의사용 서한·병원별)
 *            → `<mark data-color="#1a1a1a">` (markdownComponents.badge)
 *  - 소분류: 마크다운 헤더 h1(depth 2)·h2(depth 3)를 **레벨대로 구분**.
 *            약사용 보고서는 `# [0단계]`(h1) 아래 `## 0. 환자 의료 여정`(h2)처럼
 *            h1/h2를 계층적으로 쓰므로 같은 depth로 합치면 위계가 뭉개진다.
 *            환자용 복약지도문은 항목이 `# `(h1)뿐이라 h2가 없어도 무방.
 *            h3(자문위원 등 메타)는 항목 수 절제를 위해 제외한다.
 *
 * 모두 변환 후에도 보존되어 실제 DOM 요소로 렌더되므로, 마크다운 소스를 재파싱하지 않고
 * 렌더된 DOM을 문서 순서대로 조회한다. element 참조를 들고 있어 별도 id/앵커가 필요 없다.
 *
 * 참고: `상담필요`/`근거확인필요`(ConsultBadge/EvidenceBadge)는 `<span>`이라 mark 셀렉터에
 * 걸리지 않아 자연스럽게 제외된다.
 */

export type TocKind = "section" | "badge" | "heading";

export interface TocItem {
  /** 추출 순서 인덱스 (React key 및 식별용) */
  id: number;
  /** 계층 깊이 (0=대분류, 1=badge 중분류, 2=h1 소분류). 들여쓰기/계층 표현용 */
  depth: number;
  /** 항목 종류 (스타일 구분용) */
  kind: TocKind;
  /** 순수 텍스트 (배지·참조 링크 등 자식 요소는 textContent로 평탄화) */
  text: string;
  /** 스크롤 점프 대상 DOM 요소 */
  element: HTMLElement;
}

/**
 * badge는 markdownComponents.badge가 부여하는 data-color로 식별한다.
 * h1·h2를 수집(h3 제외)하며, 섹션 마커·badge를 함께 잡아 위계를 만든다.
 * (스크롤 시 클릭 시점 재조회에도 동일 셀렉터를 쓰므로 export)
 */
export const TOC_SELECTOR = '.section-title, mark[data-color="#1a1a1a"], h1, h2';

/** 약사용 보고서 7단계 요약 badge — 소스에서 고정 문자열로 생성되므로 텍스트로 식별 가능. */
const SUMMARY_BADGE_TEXT = "분석결과 요약";

const classify = (el: HTMLElement, text: string): { kind: TocKind; depth: number } => {
  if (el.classList.contains("section-title")) return { kind: "section", depth: 0 };
  if (el.tagName === "MARK") {
    // '분석결과 요약'은 약사용 보고서와 동급 대분류로 승격, 그 외(💊/🏥)는 중분류
    return text === SUMMARY_BADGE_TEXT
      ? { kind: "section", depth: 0 }
      : { kind: "badge", depth: 1 };
  }
  // h1 → depth 2, h2 → depth 3 (레벨대로 구분해 약사용 단계/항목 위계를 보존)
  const level = Number(el.tagName[1]);
  return { kind: "heading", depth: 1 + level };
};

/**
 * 컨테이너 DOM에서 섹션 마커·badge·h1을 문서 순서대로 수집한다.
 * @param root 본문을 포함하는 스크롤 컨테이너. null이면 빈 배열 반환.
 */
export const extractTocItems = (root: HTMLElement | null): TocItem[] => {
  if (!root) return [];

  const nodes = root.querySelectorAll<HTMLElement>(TOC_SELECTOR);

  return Array.from(nodes)
    .map((el, idx) => {
      const text = el.textContent?.trim() ?? "";
      const { kind, depth } = classify(el, text);
      return { id: idx, depth, kind, text, element: el };
    })
    .filter((item) => item.text.length > 0);
};
